import React, { useEffect, useState } from 'react';
import { MealType, UserType } from '../../common/commonTypes';
import CircularProgress from '@mui/material/CircularProgress';
import { getFavoriteMeals, saveMeal } from '../../api';
import MealCard from '../MealCard';
import Snackbar from '@mui/material/Snackbar';
import { makeMealsForSaving } from '../HomeTab/helpers';
import { DateTime } from 'luxon';
import ModalDeleteMeal from '../ModalDeleteMeal';
import ModalSaveMeal from '../ModalSaveMeal';
import Fab from '@mui/material/Fab';
import AddIcon from '@mui/icons-material/Add';
import { isUserUnsubscribed } from '../../utils/helpers';
import { Trans, useTranslation } from 'react-i18next';
import HillTopBanner from '../HillTopBanner';

const emptyMeal: MealType = {
    name: 'New Favorite Meal',
    weight: 0,
    calories: 0,
    protein: 0,
    fats: 0,
    carbsNoSugars: 0,
    sugars: 0,
};

type FavoritesType = {
    user: UserType;
};

const FavoritesTab = (props: FavoritesType) => {
    const { user } = props;
    const userIsUnsubscribed: boolean = isUserUnsubscribed(user);

    const { t, i18n } = useTranslation();

    const [loading, setLoading] = useState<boolean>(true);

    const [favs, setFavs] = useState<MealType[]>([]);

    const [favToDelete, setFavToDelete] = useState<MealType | null>(null);
    const [favToEdit, setFavToEdit] = useState<MealType | null>(null);
    const [favToCreate, setFavToCreate] = useState<MealType | null>(null);
    const [someFromFav, setSomeFromFav] = useState<MealType | null>(null);

    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const fetchFavs = async () => {
        try {
            const response = await getFavoriteMeals(user.id);
            setFavs(response.data.favMeals);
            setLoading(false);
        } catch (error) {
            alert('Failed to load Favorites.'); // todo ERROR BANNER
            console.error('Error fetching Favorites:', error);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFavs();
    }, []);

    const deleteFavHandler = async (meal: MealType) => {
        setFavToDelete(meal);
    };

    const handleDeleteCancel = () => {
        setFavToDelete(null);
    };

    const handleDeleteSuccess = () => {
        setSuccessMessage(`${favToDelete?.name} ${t('SUCCESS_MSG_deleted')}`);
        setFavToDelete(null);
        fetchFavs();
    };

    const handleEditFav = (meal: MealType) => {
        setFavToEdit(meal);
    };

    const handleCreateFav = () => {
        setFavToCreate(emptyMeal);
    };

    const onLogFav = async (meal: MealType) => {
        try {
            const confirmedMealsToSave = makeMealsForSaving([meal], user.id);
            await saveMeal(confirmedMealsToSave);
            setSuccessMessage(`${meal.name} ${t('SUCCESS_MSG_added_for_today')}!`);
        } catch (error) {
            console.error('Error submitting meal:', error);
            alert('Failed to add meal.'); // todo ERROR BANNER
        }
    };

    const handleLogSomeFromFav = (meal: MealType) => {
        setSomeFromFav(meal);
    };

    const handleSuccessSavingSomeFromFav = (mealName?: string) => {
        setSuccessMessage(`${mealName} ${t('SUCCESS_MSG_added_for_today')}`);
        setSomeFromFav(null);
    };

    const handleCancelSaving = () => {
        setFavToEdit(null);
        setFavToCreate(null);
        setSomeFromFav(null);
    };

    const handleSuccessSaving = (mealName?: string) => {
        setSuccessMessage(`${mealName} ${t('SUCCESS_MSG_saved_to_favs')}`);
        setFavToEdit(null);
        setFavToCreate(null);
        fetchFavs();
    };

    return (
        <div className="h-full">
            {loading ? (
                <div className="mt-40 text-center">
                    <CircularProgress color="primary" />
                </div>
            ) : (
                <>
                    {!favs.length && (
                        <div className="h-full flex flex-col items-center justify-center text-center text-gray-700 pb-[55px]">
                            <img
                                src="/avatar_200x200_fav.png"
                                alt=""
                                className="block w-50 h-50 mb-4"
                            />
                            <div>
                                {t('hello')}, {user.firstName}, {t('you_didnt_save_favs')}
                            </div>
                            <div className="text-sm mt-4">
                                <Trans i18nKey="click_btn_create_fav" />
                            </div>
                        </div>
                    )}

                    {Boolean(favs.length) && (
                        <>
                            {userIsUnsubscribed && <HillTopBanner height="tall" />}

                            <ul className="px-4 pt-4 pb-20">
                                {favs.map((fav: MealType, idx) => (
                                    <li key={idx}>
                                        <MealCard
                                            meal={fav}
                                            onDelete={deleteFavHandler}
                                            onEdit={handleEditFav}
                                            onLog={onLogFav}
                                            onLogSome={handleLogSomeFromFav}
                                        />
                                    </li>
                                ))}
                                <li>{userIsUnsubscribed && <HillTopBanner height="short" />}</li>
                            </ul>
                        </>
                    )}

                    <div className="absolute right-8 bottom-10">
                        <Fab color="primary" aria-label="add" onClick={handleCreateFav}>
                            <AddIcon />
                        </Fab>
                    </div>

                    {favToDelete && (
                        <ModalDeleteMeal
                            meal={favToDelete}
                            actionType="FAV"
                            onCancel={handleDeleteCancel}
                            onSuccess={handleDeleteSuccess}
                        />
                    )}

                    {favToCreate && (
                        <ModalSaveMeal
                            user={user}
                            meals={[favToCreate]}
                            actionType="CREATE_FAV"
                            onSuccess={handleSuccessSaving}
                            onCancel={handleCancelSaving}
                        />
                    )}

                    {favToEdit && (
                        <ModalSaveMeal
                            user={user}
                            meals={[favToEdit]}
                            actionType="EDIT_FAV"
                            onSuccess={handleSuccessSaving}
                            onCancel={handleCancelSaving}
                        />
                    )}

                    {someFromFav && (
                        <ModalSaveMeal
                            user={user}
                            meals={[someFromFav]}
                            actionType="SAVE_SOME_FAV"
                            onSuccess={handleSuccessSavingSomeFromFav}
                            onCancel={handleCancelSaving}
                        />
                    )}

                    {/* TODO: ABSTRACT, IMPROVE (err / success states) AND REUSE  */}
                    {successMessage && (
                        <Snackbar
                            // anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                            open
                            autoHideDuration={3000}
                            onClose={() => {
                                setSuccessMessage(null);
                            }}
                            message={successMessage}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default FavoritesTab;
